import {
  ITINERARY_HEIGHT_MODE,
  ITINERARY_HEIGHT_MODE_CONDITIONS,
  ItineraryHeightModeType,
} from '@/app/ui/trip/client-trip-page';
import { useCallback, useEffect } from 'react';

export default function useItineraryResize(
  itineraryHeightValues: {
    full: string;
    default: string;
    minimalize: string;
  },
  setItineraryHeightMode: (mode: ItineraryHeightModeType) => void,
) {
  const setupItineraryResizeHandler = useCallback(() => {
    const $itinerary = document.getElementById('planner-itinerary');
    const $handler = document.getElementById('itineraryHandler');
    const $handlerHandle = document.querySelector('#itineraryHandler span');
    if (!$handler) return () => {};

    let isMove = false;
    let startY = 0;
    let startHeight = $itinerary?.clientHeight ?? 0;
    let startMode: ItineraryHeightModeType = ITINERARY_HEIGHT_MODE.DEFAULT;

    const getStartMode = (height: number): ItineraryHeightModeType => {
      if (height === parseInt(itineraryHeightValues.full)) return ITINERARY_HEIGHT_MODE.FULL;
      if (height === parseInt(itineraryHeightValues.minimalize)) return ITINERARY_HEIGHT_MODE.MINIMALIZE;
      return ITINERARY_HEIGHT_MODE.DEFAULT;
    };

    const handlerTouchStart = (e: TouchEvent) => {
      const currentHeight = $itinerary?.clientHeight ?? 0;
      if (
        currentHeight !== parseInt(itineraryHeightValues.full) &&
        currentHeight !== parseInt(itineraryHeightValues.default) &&
        currentHeight !== parseInt(itineraryHeightValues.minimalize)
      ) {
        console.warn('크기 조절 핸들러 무시됨: 일정표 높이가 변경 중이거나 예상치 못한 상태입니다.');
        return;
      }
      startMode = getStartMode(currentHeight);
      isMove = true;
      startY = e.touches[0].clientY;
      startHeight = currentHeight;
      $itinerary?.style.setProperty('transition', 'none');
    };

    const handlerMouseStart = (e: MouseEvent) => {
      if (e.target !== $handler && e.target !== $handlerHandle) return;
      const currentHeight = $itinerary?.clientHeight ?? 0;
      if (
        currentHeight !== parseInt(itineraryHeightValues.full) &&
        currentHeight !== parseInt(itineraryHeightValues.default) &&
        currentHeight !== parseInt(itineraryHeightValues.minimalize)
      ) {
        console.warn('크기 조절 핸들러 무시됨: 일정표 높이가 변경 중이거나 예상치 못한 상태입니다.');
        return;
      }
      startMode = getStartMode(currentHeight);
      isMove = true;
      startY = e.clientY;
      startHeight = currentHeight;
      $itinerary?.style.setProperty('transition', 'none');
    };

    const handlerTouchMove = (e: TouchEvent) => {
      if (!isMove || !$itinerary) return;
      const currentY = e.touches[0].clientY;
      const currentHeight = startHeight + (startY - currentY);
      $itinerary.style.setProperty('height', `${currentHeight}px`);
    };

    const handlerMousehMove = (e: MouseEvent) => {
      if (!isMove || !$itinerary) return;
      const currentY = e.clientY;
      const currentHeight = startHeight + (startY - currentY);
      $itinerary.style.setProperty('height', `${currentHeight}px`);
    };

    const handlerEnd = (e: TouchEvent | MouseEvent) => {
      if (!isMove || !$itinerary) return;
      isMove = false;
      const endHeight = $itinerary.clientHeight ?? 0;

      let direction: 'up' | 'down' | null = null;
      if (endHeight > startHeight) direction = 'up';
      else if (endHeight < startHeight) direction = 'down';

      if (!direction) {
        $itinerary.style.setProperty('height', `${startHeight}px`);
        $itinerary?.style.setProperty('transition', 'height 50ms linear');
        return;
      }

      $itinerary.style.setProperty('transition', 'height 50ms linear');
      if (ITINERARY_HEIGHT_MODE_CONDITIONS.IS_FULL(startMode)) {
        if (direction === 'up') {
          setItineraryHeightMode(ITINERARY_HEIGHT_MODE.FULL);
          $itinerary.style.setProperty('height', `${itineraryHeightValues.full}`);
        } else {
          setItineraryHeightMode(ITINERARY_HEIGHT_MODE.DEFAULT);
          $itinerary.style.setProperty('height', `${itineraryHeightValues.default}`);
        }
      } else if (ITINERARY_HEIGHT_MODE_CONDITIONS.IS_DEFAULT(startMode)) {
        if (direction === 'up') {
          setItineraryHeightMode(ITINERARY_HEIGHT_MODE.FULL);
          $itinerary.style.setProperty('height', `${itineraryHeightValues.full}`);
        } else {
          setItineraryHeightMode(ITINERARY_HEIGHT_MODE.MINIMALIZE);
          $itinerary.style.setProperty('height', `${itineraryHeightValues.minimalize}`);
        }
      } else if (ITINERARY_HEIGHT_MODE_CONDITIONS.IS_MINIMALIZE(startMode)) {
        if (direction === 'up') {
          setItineraryHeightMode(ITINERARY_HEIGHT_MODE.DEFAULT);
          $itinerary.style.setProperty('height', `${itineraryHeightValues.default}`);
        } else {
          setItineraryHeightMode(ITINERARY_HEIGHT_MODE.MINIMALIZE);
          $itinerary.style.setProperty('height', `${itineraryHeightValues.minimalize}`);
        }
      }
    };

    $handler.addEventListener('touchstart', handlerTouchStart);
    document.addEventListener('mousedown', handlerMouseStart);
    $handler.addEventListener('touchmove', handlerTouchMove);
    document.addEventListener('mousemove', handlerMousehMove);
    $handler.addEventListener('touchend', handlerEnd);
    document.addEventListener('mouseup', handlerEnd);

    return () => {
      $handler.removeEventListener('touchstart', handlerTouchStart);
      document.removeEventListener('mousedown', handlerMouseStart);
      $handler.removeEventListener('touchmove', handlerTouchMove);
      document.removeEventListener('mousemove', handlerMousehMove);
      $handler.removeEventListener('touchend', handlerEnd);
      document.removeEventListener('mouseup', handlerEnd);
    };
  }, [itineraryHeightValues]);

  useEffect(() => {
    const cleanup = setupItineraryResizeHandler();
    return cleanup;
  }, [setupItineraryResizeHandler]);
}
